import React, { useEffect, useState } from "react";
import Dashboard from "./ui/DashboardComponents";
import SettingsPage from "./TabPages/SettingsPage";
import ReviewQueuePage from "./TabPages/ReviewQueuePage";
import BulkActionsPage from "./TabPages/BulkActionsPage";
import Form12bb from "../components/ui/Form12BB/Index";

// Icons
import { IoSettings } from "react-icons/io5";
import { CalendarAlt, RefreshCw } from "../../../../../components/icons/Icons";
import { useSelector } from "react-redux";

const TDSModuleLayout = () => {
  // Get theme from Redux store
  const themeStore = useSelector((state) => state.theme);
  const theme = themeStore?.theme || localStorage.getItem("theme") || "light";

  const pages = [
    { id: 1, label: "Dashboard", component: <Dashboard />, hash: "dashboard" },
    { id: 2, label: "TDS Form 12BB", component: <Form12bb />, hash: "form12bb" },
    { id: 3, label: "Review Queue", component: <ReviewQueuePage />, hash: "review" },
    { id: 4, label: "Bulk Actions", component: <BulkActionsPage />, hash: "bulk" },
    { id: 5, label: "Settings", component: <SettingsPage />, hash: "settings" },
  ];

  const [activeMenu, setActiveMenu] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Theme-based colors
  const colors = {
    light: {
      background: "#ffffff",
      text: "#333333",
      border: "#e0e0e0",
      tabBg: "#f5f5f5",
      tabActive: "#8b5cf6",
      tabInactive: "#666666",
      cardBg: "#ffffff",
      cardBorder: "#e0e0e0",
      lastUpdatedBg: "#f5f5f5",
      lastUpdatedBorder: "#e0e0e0",
      lastUpdatedText: "#666666",
      lastUpdatedStrong: "#333333",
      refreshGradient: "linear-gradient(90deg, #8b5cf6 0%, #6d28d9 100%)",
      refreshShadow: "rgba(139, 92, 246, 0.3)",
      iconColor: "#666666",
    },
    dark: {
      background: "#0f0f1a",
      text: "#ffffff",
      border: "#333333",
      tabBg: "transparent",
      tabActive: "#9b51e0",
      tabInactive: "#cccccc",
      cardBg: "#1a1a2a",
      cardBorder: "#333333",
      lastUpdatedBg: "rgba(255, 255, 255, 0.08)",
      lastUpdatedBorder: "rgba(255, 255, 255, 0.15)",
      lastUpdatedText: "#cccccc",
      lastUpdatedStrong: "#ffffff",
      refreshGradient: "linear-gradient(90deg, #a78bfa 0%, #8b5cf6 100%)",
      refreshShadow: "rgba(139, 92, 246, 0.4)",
      iconColor: "#aaaaaa",
    },
  };

  const currentColors = colors[theme];

  // Format as "2 min ago"
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

    return "Long ago";
  };

  // Get active tab from URL hash
  const getTabFromHash = () => {
    const hash = window.location.hash.slice(1);
    const parts = hash.split("#");
    const tabHash = parts[1] || parts[0] || "dashboard";
    const found = pages.find((p) => p.hash === tabHash);
    return found ? found.id : 1;
  };

  useEffect(() => {
    const id = getTabFromHash();
    setActiveMenu(id);

    const saved = localStorage.getItem("tdsLastUpdated");
    const now = new Date();
    setLastUpdated(saved ? new Date(saved) : now);
    localStorage.setItem("tdsLastUpdated", now.toISOString());
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const id = getTabFromHash();
      setActiveMenu(id);
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const handleTabClick = (item) => {
    const currentHash = window.location.hash.slice(1);
    const base = currentHash.split("#")[0] || "tds";
    window.location.hash = `${base}#${item.hash}`;
  };

  // Refresh ONLY TDS Module - No Full Page Reload
  const handleRefresh = () => {
    const now = new Date();
    setLastUpdated(now);
    localStorage.setItem("tdsLastUpdated", now.toISOString());

    // Dispatch custom event for child components to listen and refetch data
    window.dispatchEvent(
      new CustomEvent("tdsModuleRefresh", {
        detail: { timestamp: now },
      })
    );
  };

  const activePage = pages.find((p) => p.id === activeMenu)?.component;

  return (
    <>
      {/* Top Bar - Responsive & Clean */}
      <div
        style={{
          backgroundColor: currentColors.background,
          color: currentColors.text,
          padding: "12px 16px",
          borderBottom: `1px solid ${currentColors.border}`,
          minWidth: "100%",
          boxSizing: "border-box",
          transition: "all 0.3s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {/* Left: Title + Tabs */}
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
              <IoSettings style={{ 
                fontSize: "26px", 
                color: theme === "light" ? "#383739" : "#f5f5f5ff" 
              }} />
              <span style={{ fontSize: "20px", fontWeight: "bold", whiteSpace: "nowrap" }}>
                TDS Module
              </span>
            </div>

            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "center" }}>
              {pages.map((item) => (
                <div key={item.id} style={{ position: "relative" }}>
                  <button
                    onClick={() => handleTabClick(item)}
                    style={{
                      background: "none",
                      border: "none",
                      color: currentColors.text,
                      fontSize: "15px",
                      fontWeight: activeMenu === item.id ? "600" : "500",
                      padding: "8px 12px",
                      cursor: "pointer",
                      opacity: activeMenu === item.id ? 1 : 0.7,
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </button>

                  {activeMenu === item.id && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "-12px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "80%",
                        height: "3px",
                        backgroundColor: currentColors.tabActive,
                        borderRadius: "2px",
                        transition: "all 0.3s ease",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Last Updated + Refresh */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 16px",
                backgroundColor: currentColors.lastUpdatedBg,
                border: `1px solid ${currentColors.lastUpdatedBorder}`,
                borderRadius: "12px",
                fontSize: "13px",
                whiteSpace: "nowrap",
                transition: "all 0.3s ease",
              }}
            >
              <CalendarAlt style={{ 
                fontSize: "16px", 
                color: currentColors.iconColor 
              }} />
              <span style={{ color: currentColors.lastUpdatedText }}>
                Last Updated: <strong style={{ color: currentColors.lastUpdatedStrong }}>
                  {formatTimeAgo(lastUpdated)}
                </strong>
              </span>
            </div>

            <button
              onClick={handleRefresh}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "10px 24px",
                background: currentColors.refreshGradient,
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "12px",
                minWidth: "150px",
                boxShadow: `0 4px 15px ${currentColors.refreshShadow}`,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = `0 6px 20px ${currentColors.refreshShadow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = `0 4px 15px ${currentColors.refreshShadow}`;
              }}
            >
              <RefreshCw style={{ fontSize: "16px" }} />
              <span>Refresh Data</span>
            </button>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div style={{ 
        padding: "24px", 
        minHeight: "calc(100vh - 80px)",
        backgroundColor: theme === "light" ? "#f8f9fa" : "#0a0a14",
        color: currentColors.text,
        transition: "all 0.3s ease",
      }}>
        {activePage}
      </div>
    </>
  );
};

export default TDSModuleLayout;